import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
	FkTable,
} from '../../stores/types/content';
import { MediaItem } from '../../stores/reducers/media';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import { setSelectedMediaTemplate } from '../../stores/actions/generate-content';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { GreenroomState as GS } from '../../stores/types/types';
import { setCurrentGraphicsId } from '../../stores/actions/graphics';
import { nonEditTypes } from '../../utils/content-templates';
import StylePackLoadingSpinner from '../style-packs/style-pack-loading-spinner';
import { TABS } from '../../stores/types/tabs';
import { setActiveSubTab, setActiveTab, setRightDrawerOpen } from '../../stores/actions/tabs';
import { MEDIA_SETTINGS_TABS } from '../../stores/reducers/session';
import { ContentTemplate } from '../types/stylepacks';

interface VideoHoverOptionsProps {
	className?: string;
	generateContentTemplate?: ContentTemplate;
	mediaItem?: MediaItem;
	isHovering: boolean;
	videoRef?: HTMLVideoElement | null;
	isForShotEditorModal?: boolean;
}

function useVideoPosition(videoRef: HTMLVideoElement | null, isForShotEditorModal: boolean) {
	const [position, setPosition] = useState<DOMRect | null>(null);
	const intervalRef = useRef<NodeJS.Timer | null>(null);

	useEffect(() => {
		const updatePosition = () => {
			if (videoRef) {
				const rect = videoRef.getBoundingClientRect();
				setPosition(rect);
			}
		};

		updatePosition();
		intervalRef.current = setInterval(updatePosition, 100);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [videoRef]);

	const getPositionStyles = (): React.CSSProperties => {
		if (!position) return {};

		if (isForShotEditorModal) {
			// for shot editor modal, position relative to the modal container
			const modalContainer = document.getElementById('shot-builder-modal-main-video-container');
			const modalRect = modalContainer?.getBoundingClientRect();

			return {
				position: 'absolute',
				top: `${position.top - (modalRect?.top ?? 0)}px`,
				left: `${position.left - (modalRect?.left ?? 0)}px`,
				width: `${position.width}px`,
				height: `${position.height}px`,
				pointerEvents: 'none',
			};
		}

		// for regular view, position relative to viewport with header offset
		return {
			position: 'absolute',
			top: `calc(${position.top}px - var(--meeting-header-height))`,
			left: `${position.left}px`,
			width: `${position.width}px`,
			height: `${position.height}px`,
			pointerEvents: 'none',
		};
	};

	return { position, getPositionStyles };
}

export default function VideoHoverOptions(props: VideoHoverOptionsProps) {
	const {
		className,
		generateContentTemplate,
		mediaItem,
		isHovering,
		videoRef,
		isForShotEditorModal = false,
	} = props;

	const dispatch = useDispatch();
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const { getPositionStyles } = useVideoPosition(videoRef || null, isForShotEditorModal);

	if (!mediaItem || nonEditTypes.includes(generateContentTemplate?.type ?? '')) {
		return null;
	}

	const handleEditClick = () => {
		if (mediaItem) {
			dispatch(setSelectedMediaTemplate(mediaItem, generateContentTemplate?.type ?? ''));
			dispatch(setCurrentGraphicsId(0));
			if (!isForShotEditorModal) {
				dispatch(setRightDrawerOpen(true));
				dispatch(setActiveTab(TABS.GENERATE_MEDIA_CONTENT));
				dispatch(setActiveSubTab(TABS.GENERATE_MEDIA_CONTENT, MEDIA_SETTINGS_TABS.CONTENT));
			}
		}
	};

	const shouldShowEditButton = generateContentTemplate &&
		(roomState === ROOM_STATES.EDITING || isForShotEditorModal);

	return (
		<div
			className={classNames('video-hover-options', className)}
			style={getPositionStyles()}
		>
			<StylePackLoadingSpinner
				type={FkTable.media}
				id={mediaItem.id}
				isForShotEditorModal={isForShotEditorModal}
			/>
			<div className={classNames('video-hover-options-buttons', isHovering && 'hover')}>
				{shouldShowEditButton && (
					<div
						className="video-hover-options-button"
						onClick={handleEditClick}
					>
						<Icon
							name={ICONS.EDIT}
							color={COLORS.WHITE}
							size={12}
						/>
					</div>
				)}
			</div>
		</div>
	);
}